import React, { useCallback, useEffect, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ImageErrorPlaceholder } from '@yme/atoms/image-error-placeholder';
import { buildKey } from '@yme/common/index';
import { useImageError } from '@yme/hooks/index';
import { escapeImageUrl } from '@yme/utils/common';
import { getImageSources } from './helpers';
import { ImageStyled, ImageStyledWrapper } from './styled';
/**
 * @param src - string
 * @param fallbackSrc - string; alternative source of src image
 * @param alt - string | optional prop
 * @param srcSet - object | optional prop
 * @param caption - string | title text
 * @param width - number | optional prop
 * @param height - number | optional prop
 * @param dataTestId - string | prop for automation testing
 * @param maxHeight - number | optional prop
 * @param decorative - boolean | optional prop | default - false
 * @param draggable - boolean | optional prop | default - false
 * @param onError - function | work when image didn't load. Error case is result return default image.
 * @param displayPopupTitle - boolean | When checked, the caption won't be
 * displayed below the image, but as a pop-up displayed by some browsers when hovering over the image.
 * @returns JSX.Element | null
 */
export function Image(props) {
    const { src = '', alt = '', srcSet = [], caption = '', width = '100%', height = 'auto', maxHeight = 'none', isFullHeight = true, // TODO(A.N.): change for any height
    decorative = false, draggable = false, role, displayPopupTitle = false, dataTestId = 'atom', aspectRatio, errorPlaceholderAspectRatio, objectFit, errorPlaceholderHasBackground = true, onError, placeholderComponent, fallbackSrc = '', lazyLoadImagePlaceholder, lazyLoadImagePlaceholderSrc, visibleByDefault, threshold, isLazyLoadImage, loading = 'lazy', ...rest } = props;
    const [sourceImage, setSourceImage] = useState(src);
    const [imageUrl, setImageUrl] = useState(escapeImageUrl(sourceImage));
    const [errorOccurred, setErrorOccurred] = useState(false);
    const { isImageLoading, onErrorHandler } = useImageError();
    useEffect(() => {
        if (src !== sourceImage) {
            setSourceImage(src);
            setImageUrl(escapeImageUrl(src));
        }
    }, [sourceImage, src]);
    const handleImageError = useCallback(() => {
        if (onError) {
            onError();
        }
        else {
            if (!errorOccurred) {
                setImageUrl(escapeImageUrl(fallbackSrc));
                setErrorOccurred(true);
            }
            if (errorOccurred) {
                onErrorHandler();
            }
        }
    }, [errorOccurred, fallbackSrc, onError, onErrorHandler]);
    if (!src || !isImageLoading) {
        return placeholderComponent ? (placeholderComponent) : (React.createElement(ImageErrorPlaceholder, { aspectRatio: errorPlaceholderAspectRatio || aspectRatio, hasBackground: errorPlaceholderHasBackground }));
    }
    const sources = getImageSources(srcSet);
    const titleRole = displayPopupTitle &&
        caption && {
        title: caption,
    };
    const dataTestIdImage = buildKey(dataTestId, 'image');
    return (React.createElement(ImageStyledWrapper, { isFullHeight: isFullHeight, ...rest },
        React.createElement("picture", null,
            sources,
            isLazyLoadImage ? (React.createElement(LazyLoadImage, { src: imageUrl, srcSet: "", sizes: "", threshold: threshold, onError: handleImageError, placeholder: lazyLoadImagePlaceholder, placeholderSrc: lazyLoadImagePlaceholderSrc, visibleByDefault: visibleByDefault, width: width, height: height, alt: alt, draggable: draggable, "data-testid": dataTestIdImage })) : (React.createElement(ImageStyled, { ...titleRole, ...(role && {
                    role,
                }), src: imageUrl, onError: handleImageError, alt: (!decorative && alt) || '', width: width, height: height, maxHeight: maxHeight, draggable: draggable, aspectRatio: aspectRatio, objectFit: objectFit, "data-testid": dataTestIdImage, loading: loading ?? undefined }))),
        !displayPopupTitle && caption && React.createElement("p", null, caption)));
}
Image.displayName = 'Image';
